/**
 * useStockSettings — leitura/gravação das configurações de baixa de estoque.
 *
 * **Online-only para GRAVAR**, como as categorias do financeiro e os modelos de
 * mensagem: é configuração, não dado de negócio, e a fila offline mapeia
 * 1 operação → 1 uuid (CLAUDE.md #30c/#65). A LEITURA, ao contrário, precisa
 * funcionar sem rede — é ela que decide se o prompt de baixa aparece — então
 * vem de `initialData` do cache com `refetchOnMount: 'always'` (#11/#80).
 *
 * Empresa sem linha na tabela → `DEFAULT_STOCK_SETTINGS`, que é o comportamento
 * anterior à feature. Nenhuma linha é criada até o usuário mudar algo (nada de
 * seed na leitura — a armadilha #37).
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import {
  DEFAULT_STOCK_SETTINGS,
  stockSettingsFromRow,
  toStockSettingsColumns,
  triggerAction,
  type StockSettings,
  type StockTriggerKey,
} from '@/lib/stockSettings';

const KEY = 'stock-settings';
const cacheKey = (companyId: string) => `orcamigo_stock_settings_${companyId}`;

function readCache(companyId: string | null): StockSettings | undefined {
  if (!companyId) return undefined;
  try {
    const raw = localStorage.getItem(cacheKey(companyId));
    return raw ? stockSettingsFromRow(JSON.parse(raw)) : undefined;
  } catch {
    return undefined;
  }
}

function writeCache(companyId: string, row: Record<string, unknown>): void {
  try {
    localStorage.setItem(cacheKey(companyId), JSON.stringify(row));
  } catch {
    // cota — a leitura degrada para os padrões, que é o comportamento antigo
  }
}

export function useStockSettings() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [KEY, companyId],
    enabled: Boolean(user?.id && companyId),
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => readCache(companyId),
    queryFn: async (): Promise<StockSettings> => {
      if (!companyId) return DEFAULT_STOCK_SETTINGS;
      const { data, error } = await supabase
        .from('stock_settings' as any)
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();
      if (error) throw error;
      // `stock_settings` ainda não está no `types.ts` gerado (regenerá-lo aqui
      // traria colunas de WIP alheio junto), por isso os casts via `unknown`.
      const row = data as unknown as Record<string, unknown> | null;
      if (row) writeCache(companyId, row);
      return stockSettingsFromRow(row);
    },
  });

  const settings = query.data ?? DEFAULT_STOCK_SETTINGS;

  /** O que fazer quando um gatilho dispara — 'off' | 'ask' | 'auto'. */
  const actionFor = useCallback(
    (key: StockTriggerKey) => triggerAction(settings, key),
    [settings],
  );

  const save = useMutation({
    // `offlineFirst` mesmo sendo uma escrita ONLINE-ONLY, e é justamente por
    // isso: com o default 'online' o TanStack PAUSARIA a mutation ao perder a
    // rede com a tela aberta (CLAUDE.md #41) e o `checkNetworkStatus` abaixo
    // nunca rodaria — o usuário veria "Salvando…" para sempre em vez do aviso.
    // Aqui a opção serve para FALHAR RÁPIDO com mensagem, não para enfileirar.
    networkMode: 'offlineFirst' as const,
    mutationFn: async (next: StockSettings) => {
      if (!companyId) throw new Error('Nenhuma empresa ativa');
      if (!(await checkNetworkStatus())) {
        throw new Error('offline');
      }
      // As 10 colunas de uma vez — nenhum caminho as enumera à mão (#33/#62).
      const row = { company_id: companyId, ...toStockSettingsColumns(next), updated_at: new Date().toISOString() };
      const { error } = await supabase
        .from('stock_settings' as any)
        .upsert(row as any, { onConflict: 'company_id' });
      if (error) throw error;
      writeCache(companyId, row);
      queryClient.setQueryData([KEY, companyId], next);
      return next;
    },
    onError: (error: Error) => {
      if (error?.message === 'offline') {
        toast.warning('Configurações de estoque exigem conexão. Conecte-se e tente de novo.');
        return;
      }
      toast.error('Não foi possível salvar as configurações de estoque.');
    },
  });

  return { ...query, settings, actionFor, save };
}
